import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, Sparkles, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { resolveBackendUrl } from '../lib/utils';

// Create axios instance with default config
const apiClient = axios.create({
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

const BACKEND_URL = resolveBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Debug: Log the API URL (remove in production)
if (process.env.NODE_ENV === 'development') {
  console.log('Backend URL:', BACKEND_URL);
  console.log('API Base URL:', API);
}

const PredictionPage = () => {
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    diameter_km: '',
    distance_au: '',
    composition_type: 'S',
    estimated_value_billion: '',
    delta_v_km_s: '',
    orbit_eccentricity: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadSampleData = () => {
    setFormData({
      name: '16 Psyche',
      diameter_km: '226',
      distance_au: '2.921',
      composition_type: 'M',
      estimated_value_billion: '10000',
      delta_v_km_s: '8.5',
      orbit_eccentricity: '0.134',
    });
    toast.success('Sample data loaded!');
  };

  const handlePredict = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPrediction(null);

    try {
      console.log('Making request to:', `${API}/predict`);
      const response = await apiClient.post(`${API}/predict`, {
        name: formData.name,
        diameter_km: parseFloat(formData.diameter_km),
        distance_au: parseFloat(formData.distance_au),
        composition_type: formData.composition_type,
        estimated_value_billion: parseFloat(formData.estimated_value_billion),
        delta_v_km_s: parseFloat(formData.delta_v_km_s),
        orbit_eccentricity: parseFloat(formData.orbit_eccentricity),
      });

      setPrediction(response.data);
      toast.success('Prediction completed!');
    } catch (error) {
      console.error('Prediction error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        apiUrl: `${API}/predict`
      });

      let errorMessage = 'Prediction failed: ';

      if (error.code === 'ECONNABORTED') {
        // Axios timeout
        errorMessage += 'The server took too long to respond. This can happen if the backend is waking up. Please wait a moment and try again.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        errorMessage += 'Unable to reach the backend server. Please check your connection and try again.';
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (risk) => {
    switch (risk.toLowerCase()) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center">
              <Target className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold gradient-text mb-3" data-testid="page-title">Profitability Prediction</h1>
          <p className="text-gray-400" data-testid="page-description">ML-powered asteroid mining profitability analysis</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card border-white/10" data-testid="prediction-form">
              <CardHeader>
                <CardTitle className="text-white">Asteroid Parameters</CardTitle>
                <CardDescription className="text-gray-400">Enter asteroid data for analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePredict} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-gray-300">Asteroid Name</Label>
                    <Input
                      id="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="e.g., 16 Psyche"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="diameter" className="text-gray-300">Diameter (km)</Label>
                      <Input
                        id="diameter"
                        data-testid="input-diameter"
                        type="number"
                        step="0.01"
                        value={formData.diameter_km}
                        onChange={(e) => handleInputChange('diameter_km', e.target.value)}
                        placeholder="226.0"
                        required
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="distance" className="text-gray-300">Distance (AU)</Label>
                      <Input
                        id="distance"
                        data-testid="input-distance"
                        type="number"
                        step="0.001"
                        value={formData.distance_au}
                        onChange={(e) => handleInputChange('distance_au', e.target.value)}
                        placeholder="2.921"
                        required
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="composition" className="text-gray-300">Composition Type</Label>
                    <Select value={formData.composition_type} onValueChange={(value) => handleInputChange('composition_type', value)}>
                      <SelectTrigger data-testid="select-composition" className="bg-white/5 border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-white/10">
                        <SelectItem value="S" data-testid="composition-s">S-type (Silicate)</SelectItem>
                        <SelectItem value="C" data-testid="composition-c">C-type (Carbonaceous)</SelectItem>
                        <SelectItem value="M" data-testid="composition-m">M-type (Metallic)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="value" className="text-gray-300">Estimated Value (Billion $)</Label>
                    <Input
                      id="value"
                      data-testid="input-value"
                      type="number"
                      step="0.1"
                      value={formData.estimated_value_billion}
                      onChange={(e) => handleInputChange('estimated_value_billion', e.target.value)}
                      placeholder="10000"
                      required
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="deltav" className="text-gray-300">Delta-V (km/s)</Label>
                      <Input
                        id="deltav"
                        data-testid="input-deltav"
                        type="number"
                        step="0.1"
                        value={formData.delta_v_km_s}
                        onChange={(e) => handleInputChange('delta_v_km_s', e.target.value)}
                        placeholder="8.5"
                        required
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="eccentricity" className="text-gray-300">Eccentricity</Label>
                      <Input
                        id="eccentricity"
                        data-testid="input-eccentricity"
                        type="number"
                        step="0.001"
                        value={formData.orbit_eccentricity}
                        onChange={(e) => handleInputChange('orbit_eccentricity', e.target.value)}
                        placeholder="0.134"
                        required
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 cosmic-button"
                      data-testid="predict-submit-button"
                    >
                      {loading ? 'Analyzing...' : 'Predict Profitability'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={loadSampleData}
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      data-testid="load-sample-button"
                    >
                      Load Sample
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            {prediction ? (
              <Card className="glass-card border-white/10" data-testid="prediction-results">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-cyan-400" />
                    Prediction Results
                  </CardTitle>
                  <CardDescription className="text-gray-400">{prediction.asteroid_name}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Profitability Status */}
                  <div className="text-center p-6 rounded-lg bg-gradient-to-br from-white/5 to-white/10" data-testid="profitability-status">
                    {prediction.is_profitable ? (
                      <CheckCircle className="w-16 h-16 mx-auto mb-3 text-green-400" />
                    ) : (
                      <AlertTriangle className="w-16 h-16 mx-auto mb-3 text-red-400" />
                    )}
                    <div className={`text-2xl font-bold mb-2 ${prediction.is_profitable ? 'text-green-400' : 'text-red-400'}`}>
                      {prediction.is_profitable ? 'PROFITABLE' : 'NOT PROFITABLE'}
                    </div>
                    <div className="text-gray-400 text-sm">Confidence: {(prediction.probability * 100).toFixed(1)}%</div>
                  </div>

                  {/* Metrics */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5" data-testid="roi-metric">
                      <span className="text-gray-400">Predicted ROI</span>
                      <span className="text-xl font-bold text-cyan-400 flex items-center gap-1">
                        <TrendingUp className="w-5 h-5" />
                        {prediction.predicted_roi}%
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-lg bg-white/5" data-testid="risk-metric">
                      <span className="text-gray-400">Risk Level</span>
                      <span className={`text-xl font-bold ${getRiskColor(prediction.risk_level)}`}>
                        {prediction.risk_level}
                      </span>
                    </div>

                    <div className="p-4 rounded-lg bg-white/5" data-testid="recommendation">
                      <div className="text-sm text-gray-400 mb-2">Recommendation</div>
                      <p className="text-white text-sm leading-relaxed">{prediction.recommendation}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card border-white/10" data-testid="prediction-placeholder">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Target className="w-16 h-16 text-gray-600 mb-4" />
                  <p className="text-gray-500 text-center">
                    Enter asteroid parameters and click "Predict" to see results
                  </p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PredictionPage;
